import {
	DATE_FORMATS,
	DonationStatus,
	DonationTypeLabel,
	TableTitles,
} from "@/constants";
import { Image, Space, Tag } from "antd";
import { ColumnsType, TableProps } from "antd/es/table";
import dayjs from "dayjs";
import { AntdTable } from "../common";

interface DonationTableProps extends Omit<
	TableProps<Donation>,
	"dataSource" | "columns"
> {
	dataSource: Donation[];
	renderColumnKeys: string[];
	TableActionComponent?: React.ComponentType<{ record: Donation }>;
}

export function DonationTable({
	dataSource,
	renderColumnKeys,
	TableActionComponent,
	...props
}: DonationTableProps) {
	const columns: ColumnsType<Donation> = [
		{
			title: TableTitles.donation_date,
			dataIndex: "donatedAt",
			key: "date",
			render: (value: string) =>
				dayjs(value).format(DATE_FORMATS.DAY_MONTH_FORMAT),
			minWidth: 160,
		},
		{
			title: TableTitles.donor_name,
			dataIndex: ["donor", "name"],
			key: "donor_name",
			onCell: () => ({
				style: {
					maxWidth: "400px",
				},
			}),
		},
		{
			title: TableTitles.donor_phone,
			dataIndex: ["donor", "phone"],
			key: "donor_phone",
			minWidth: 160,
		},
		{
			title: TableTitles.retreat_to_which_donated,
			dataIndex: ["meditationEvent", "title"],
			key: "retreat_name",
			render: (value, record) =>
				value +
				` (${record.meditationEvent?.level.name} - ${dayjs(
					record.meditationEvent?.fromDate,
				).format(DATE_FORMATS.MONTH_FORMAT)})`,
		},
		{
			title: TableTitles.retreat_level,
			dataIndex: ["meditationEvent", "level", "name"],
			key: "retreat_level",
		},
		{
			title: TableTitles.donor_address,
			dataIndex: ["donor", "address"],
			key: "donor_address",
			render: (value, record) =>
				record.donor?.address +
				"၊ " +
				record.donor?.township +
				"၊ " +
				record.donor?.state,
			onCell: () => ({
				style: {
					maxWidth: "200px",
				},
			}),
		},
		{
			title: TableTitles.donation_type,
			dataIndex: "donationType",
			key: "type",
			render: (value: DonationType) => DonationTypeLabel[value],
		},
		{
			title: TableTitles.donation_amount,
			dataIndex: "amount",
			key: "amount",
			render: (value: number) =>
				value === 0
					? "-"
					: value.toLocaleString("en-US", {
							style: "currency",
							currency: "MMK",
							minimumSignificantDigits: 1,
						}),
		},
		{
			title: TableTitles.donation_payment_method,
			dataIndex: ["payment", "paymentType"],
			key: "payment_method",
			render: (value, record) => (
				<Space>
					<Image
						src={record.payment?.imageUrl || ""}
						alt="Payment Method"
						width={48}
						height={48}
						className=" object-cover rounded-sm"
						preview={false}
					/>
					<span className=" font-poppins">{value}</span>
				</Space>
			),
			align: "center",
		},
		{
			title: TableTitles.status,
			dataIndex: "status",
			key: "status",
			render: (value: DonationStatus) => (
				<Tag
					color={
						value === DonationStatus.PENDING ? "yellow" : "green"
					}
				>
					{value}
				</Tag>
			),
		},
		{
			title: TableTitles.action,
			key: "action",
			render: (value: string, record) =>
				TableActionComponent ? (
					<TableActionComponent record={record} />
				) : null,
			fixed: "right",
			minWidth: 100,
			align: "center",
		},
	];

	const renderColumns = columns.filter((column) =>
		renderColumnKeys.includes(column.title as string),
	);

	return (
		<>
			<AntdTable
				columns={renderColumns}
				rowKey="id"
				dataSource={dataSource}
				{...props}
			/>
		</>
	);
}
